/**
 * Página de auditoria de acessos a módulos.
 *
 * Responde à pergunta: "quem tem acesso a cada módulo?"
 *
 * Layout em 2 colunas:
 * - Esquerda: lista de todos os módulos com contadores agregados
 * - Direita: detalhe do módulo selecionado (lista de usuários)
 */
import React, { useState, useEffect } from 'react';
import { Shield, Users, ChevronRight, RefreshCw, Lock, Unlock } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';

// Toda seção do catálogo (crm.modulos.secao) precisa estar aqui, senão o módulo
// some da auditoria e do cadeado (caso Serviços, 09/2026).
const NOMES_SECOES = {
  operacional: 'Operacional',
  analises: 'Análises',
  comunicacoes: 'Comunicações',
  administracao: 'Administração',
  servicos: 'Serviços',
  universidade: 'Universidade',
};

const CORES_SECOES = {
  operacional: '#3498db',
  analises: '#27ae60',
  comunicacoes: '#f39c12',
  administracao: '#9b59b6',
  servicos: '#20a490',
  universidade: '#16a085',
};

const AdminAuditoriaModulos = () => {
  const [modulos, setModulos] = useState([]);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [moduloSelecionado, setModuloSelecionado] = useState(null);
  const [usuariosDoModulo, setUsuariosDoModulo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);

  useEffect(() => {
    carregar();
  }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await authAPI.auditoriaModulos();
      setModulos(data.modulos || []);
      setTotalUsuarios(data.total_usuarios_ativos || 0);
    } catch (err) {
      toast.error('Erro ao carregar auditoria de módulos');
    }
    setLoading(false);
  };

  const verDetalhe = async (modulo) => {
    setModuloSelecionado(modulo);
    setUsuariosDoModulo(null);
    setLoadingDetalhe(true);
    try {
      const { data } = await authAPI.usuariosDoModulo(modulo.codigo);
      setUsuariosDoModulo(data.usuarios || []);
    } catch (err) {
      toast.error('Erro ao carregar usuários do módulo');
    }
    setLoadingDetalhe(false);
  };

  // Agrupa módulos por seção pra renderização
  const modulosPorSecao = {};
  modulos.forEach((m) => {
    if (!modulosPorSecao[m.secao]) modulosPorSecao[m.secao] = [];
    modulosPorSecao[m.secao].push(m);
  });

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.5rem', margin: 0 }}>
            <Shield size={24} />
            Auditoria de Acessos a Módulos
          </h1>
          <p style={{ color: 'var(--gray-600)', marginTop: '0.25rem', fontSize: '0.9rem' }}>
            {totalUsuarios} usuários ativos · {modulos.length} módulos catalogados
          </p>
        </div>
        <button className="btn btn-secondary" onClick={carregar} disabled={loading}>
          <RefreshCw size={16} /> {loading ? 'Carregando...' : 'Atualizar'}
        </button>
      </div>

      <CadeadoPanel />

      {loading && <p>Carregando módulos...</p>}

      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Coluna esquerda: lista de módulos */}
          <div>
            {Object.entries(NOMES_SECOES).map(([secao, nomeSecao]) => {
              const modulosDaSecao = modulosPorSecao[secao] || [];
              if (modulosDaSecao.length === 0) return null;
              return (
                <div key={secao} style={{ marginBottom: '1.5rem' }}>
                  <div style={{
                    fontSize: '0.75rem', fontWeight: 700, color: CORES_SECOES[secao],
                    textTransform: 'uppercase', marginBottom: '0.5rem', letterSpacing: '0.05em'
                  }}>
                    {nomeSecao}
                  </div>
                  {modulosDaSecao.map((m) => {
                    const ehSelecionado = moduloSelecionado?.codigo === m.codigo;
                    return (
                      <div
                        key={m.codigo}
                        onClick={() => verDetalhe(m)}
                        style={{
                          padding: '0.75rem 1rem',
                          marginBottom: '0.4rem',
                          border: ehSelecionado ? `2px solid ${CORES_SECOES[secao]}` : '1px solid var(--gray-200)',
                          borderLeftWidth: '4px',
                          borderLeftColor: CORES_SECOES[secao],
                          borderRadius: '6px',
                          cursor: 'pointer',
                          background: ehSelecionado ? 'var(--gray-50)' : 'white',
                          transition: 'background 0.15s',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}
                        onMouseEnter={(e) => { if (!ehSelecionado) e.currentTarget.style.background = 'var(--gray-50)'; }}
                        onMouseLeave={(e) => { if (!ehSelecionado) e.currentTarget.style.background = 'white'; }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{m.nome}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.15rem' }}>
                            <code style={{ background: 'var(--gray-100)', padding: '1px 5px', borderRadius: '3px' }}>
                              {m.codigo}
                            </code>
                            {m.total_extras > 0 && (
                              <span style={{ marginLeft: '0.5rem', color: '#27ae60' }}>+{m.total_extras} extras</span>
                            )}
                            {m.total_negados > 0 && (
                              <span style={{ marginLeft: '0.5rem', color: '#e74c3c' }}>−{m.total_negados} negados</span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--gray-700)' }}>
                            <Users size={14} />
                            <span style={{ fontWeight: 700 }}>{m.total_usuarios}</span>
                          </div>
                          <ChevronRight size={16} color="var(--gray-400)" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Coluna direita: detalhe do módulo */}
          <div style={{ position: 'sticky', top: '1rem', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto' }}>
            {!moduloSelecionado && (
              <div style={{
                padding: '3rem', textAlign: 'center', color: 'var(--gray-500)',
                background: 'var(--gray-50)', borderRadius: '8px', border: '1px dashed var(--gray-300)'
              }}>
                <Shield size={48} style={{ opacity: 0.3 }} />
                <p style={{ marginTop: '1rem' }}>Selecione um módulo à esquerda para ver quem tem acesso</p>
              </div>
            )}

            {moduloSelecionado && (
              <div style={{ background: 'white', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '1.25rem' }}>
                <div style={{ borderBottom: '1px solid var(--gray-200)', paddingBottom: '0.75rem', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{moduloSelecionado.nome}</h3>
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                    <code style={{ background: 'var(--gray-100)', padding: '1px 5px', borderRadius: '3px' }}>
                      {moduloSelecionado.codigo}
                    </code>
                    <span style={{ marginLeft: '0.5rem' }}>· {NOMES_SECOES[moduloSelecionado.secao]}</span>
                  </div>
                </div>

                {/* Resumo por perfil */}
                {moduloSelecionado.por_perfil && Object.keys(moduloSelecionado.por_perfil).length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                      Por perfil
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {Object.entries(moduloSelecionado.por_perfil).map(([perfil, count]) => (
                        <span key={perfil} style={{
                          padding: '3px 8px', background: 'var(--gray-100)', borderRadius: '12px',
                          fontSize: '0.8rem', fontWeight: 600
                        }}>
                          {perfil}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {loadingDetalhe && <p>Carregando usuários...</p>}

                {!loadingDetalhe && usuariosDoModulo && (
                  <>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                      Usuários com acesso ({usuariosDoModulo.length})
                    </div>
                    {usuariosDoModulo.length === 0 && (
                      <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                        Nenhum usuário ativo tem acesso a este módulo.
                      </p>
                    )}
                    {usuariosDoModulo.map((u) => (
                      <div key={u.id} style={{
                        padding: '0.6rem 0.75rem', borderBottom: '1px solid var(--gray-100)',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{u.nome_completo}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                            {u.email} · <em>{u.perfil}</em>
                          </div>
                        </div>
                        {u.origem === 'extra' ? (
                          <span style={{
                            fontSize: '0.7rem', padding: '2px 6px', borderRadius: '3px',
                            background: '#e8f5e9', color: '#27ae60', fontWeight: 600
                          }}>
                            EXTRA
                          </span>
                        ) : (
                          <span style={{
                            fontSize: '0.7rem', padding: '2px 6px', borderRadius: '3px',
                            background: 'var(--gray-100)', color: 'var(--gray-600)'
                          }}>
                            perfil
                          </span>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Painel do cadeado de módulos ──────────────────────────────────────────
// Interruptor global (liga/desliga o cadeado para TODOS os não-admins) +
// liberação por módulo. Com o interruptor desligado, ninguém é bloqueado.
const CadeadoPanel = () => {
  const [ativo, setAtivo] = useState(false);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(null); // codigo em processamento

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await authAPI.getCadeadoConfig();
      setAtivo(!!data.cadeados_ativos);
      setModulos(data.modulos || []);
    } catch {
      toast.error('Erro ao carregar configuração de cadeados');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, []);

  const toggleInterruptor = async () => {
    const novo = !ativo;
    setSalvando('__interruptor__');
    try {
      await authAPI.setCadeadoInterruptor(novo);
      setAtivo(novo);
      toast.success(novo
        ? 'Cadeados ATIVADOS: não-admins agora veem os módulos bloqueados'
        : 'Cadeados desativados: todos voltam a acessar normalmente');
    } catch {
      toast.error('Erro ao alterar o interruptor');
    }
    setSalvando(null);
  };

  const toggleModulo = async (m) => {
    if (m.fixo_liberado) return;
    setSalvando(m.codigo);
    try {
      if (m.liberado) await authAPI.bloquearModulo(m.codigo);
      else await authAPI.liberarModulo(m.codigo);
      setModulos((prev) => prev.map((x) => x.codigo === m.codigo ? { ...x, liberado: !x.liberado } : x));
    } catch {
      toast.error('Erro ao alterar o módulo');
    }
    setSalvando(null);
  };

  return (
    <div style={{
      background: 'white', border: '1px solid var(--gray-200)', borderRadius: '8px',
      padding: '1.25rem', marginBottom: '1.5rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Lock size={18} /> Cadeado de Módulos
          </h3>
          <p style={{ color: 'var(--gray-600)', fontSize: '0.85rem', marginTop: '0.35rem', maxWidth: 560 }}>
            Quando <b>ativado</b>, todos os usuários que não são administradores passam a ver os
            módulos <b>bloqueados</b> (cadeado), exceto os que você liberar abaixo. Administradores
            nunca são bloqueados. Buscar Beneficiário e Kanban ficam sempre liberados.
          </p>
        </div>
        <button
          onClick={toggleInterruptor}
          disabled={salvando === '__interruptor__'}
          style={{
            padding: '0.6rem 1.1rem', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: ativo ? '#e74c3c' : 'var(--brand, #20a490)', color: ativo ? '#fff' : 'var(--brand-ink, #fff)', fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {ativo ? 'Desativar cadeados' : 'Ativar cadeados'}
        </button>
      </div>

      <div style={{
        marginTop: '0.75rem', fontSize: '0.82rem', fontWeight: 600,
        color: ativo ? '#e67e22' : 'var(--gray-500)',
      }}>
        Status: {ativo ? 'ATIVO (não-admins veem cadeados)' : 'Desativado (ninguém é bloqueado)'}
      </div>

      {loading ? (
        <p style={{ marginTop: '1rem', color: 'var(--gray-500)' }}>Carregando módulos...</p>
      ) : (
        <div style={{
          marginTop: '1rem', display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.5rem',
        }}>
          {modulos.map((m) => {
            const liberado = m.liberado || m.fixo_liberado;
            return (
              <div key={m.codigo} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.5rem 0.75rem', border: '1px solid var(--gray-200)', borderRadius: 6,
                opacity: m.fixo_liberado ? 0.7 : 1,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.nome}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{m.codigo}</div>
                </div>
                <button
                  onClick={() => toggleModulo(m)}
                  disabled={m.fixo_liberado || salvando === m.codigo}
                  title={m.fixo_liberado ? 'Sempre liberado' : (liberado ? 'Clique para bloquear' : 'Clique para liberar')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '0.35rem 0.6rem',
                    borderRadius: 6, border: '1px solid var(--gray-200)',
                    cursor: m.fixo_liberado ? 'default' : 'pointer',
                    background: liberado ? '#e8f5e9' : 'var(--gray-100)',
                    color: liberado ? '#27ae60' : 'var(--gray-600)', fontSize: '0.78rem', fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {liberado ? <Unlock size={13} /> : <Lock size={13} />}
                  {liberado ? 'Liberado' : 'Bloqueado'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminAuditoriaModulos;
